import React, { useRef, useState } from 'react';
import down_arrow from './../assets/images/down_arrow.svg';
import { FaPlus, FaTrash } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import useOutsideClick from '../hooks/useOutsideClick';

function VegetableItems({ allVegetables, vegetableRows, setVegetableRows }) {
    const { t } = useTranslation();
    const [isVegetableList, setIsVegetableList] = useState(false);
    const [isPopover, setIsPopover] = useState(null);
    const [searchInput, setSearchInput] = useState('');
    const popoverRef = useRef(null);
    const searchInputRef = useRef(null);

    useOutsideClick(popoverRef, () => {
        setIsPopover(null);
    });

    React.useEffect(() => {
        if (isPopover !== null && searchInputRef.current) {
            setTimeout(() => {
                searchInputRef.current.focus();
            }, 50);
        }
    }, [isPopover]);


    const unitTypeList = [
        { key: 'KGS', name: 'કિગ્રા (KGS)' },
        { key: 'Gram', name: 'ગ્રામ (Gram)' },
        { key: 'Liter', name: 'લિટર (Liter)' },
        { key: 'ML', name: 'મિલીલીટર (ML)' },
        { key: 'Pieces', name: 'નંગ (Pieces)' }
    ];

    const showList = () => {
        setIsVegetableList(!isVegetableList);
    };

    const togglePopover = (index) => {
        setIsPopover(isPopover === index ? null : index);
    };

    const handleSearchChange = (e) => {
        setSearchInput(e.target.value);
    };

    const handleVegetableClick = (index, vegetable) => {
        setVegetableRows(prevRows =>
            prevRows.map((row, i) =>
                i === index ? { ...row, vegetable, unitType: vegetable.unitType } : row
            )
        );
        setIsPopover(null);
        setSearchInput('');
    };

    const addNewRow = () => {
        setVegetableRows([...vegetableRows, { vegetable: null, quantity: '' }]);
        setSearchInput('');
    };

    const removeRow = (index) => {
        setVegetableRows(prevRows => prevRows.filter((_, i) => i !== index));
        setSearchInput('');
    };

    const clearRow = (index) => {
        setVegetableRows(prevRows =>
            prevRows.map((row, i) =>
                i === index ? { vegetable: null, unitType: '', quantity: '' } : row
            )
        );
        setSearchInput('');
    };

    const isLastRowFilled = () => {
        const lastRow = vegetableRows[vegetableRows.length - 1];
        return lastRow.vegetable && lastRow.unitType?.trim() && lastRow.quantity?.trim();
    };

    const handleChange = (index, e) => {
        const { name, value } = e.target;
        setVegetableRows(prevRows =>
            prevRows.map((row, i) =>
                i === index ? { ...row, [name]: value } : row
            )
        );
    };

    return (
        <div className="bg-white rounded-2xl shadow-card border border-gray-100">
            {/* Header */}
            <div
                className={`bg-gradient-to-r from-green-500 to-green-600 px-6 py-4 cursor-pointer ${isVegetableList ? 'rounded-t-2xl' : 'rounded-2xl'}`}
                onClick={(e) => {
                    e.stopPropagation();
                    showList();
                }}
            >
                <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                        <div className="w-8 h-8 bg-white bg-opacity-20 rounded-lg flex items-center justify-center">
                            <svg
                                className="w-5 h-5 text-white"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2H5a2 2 0 00-2-2z"
                                />
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M8 5a2 2 0 012-2h4a2 2 0 012 2v2H8V5z"
                                />
                            </svg>
                        </div>
                        <div>
                            <h3 className="text-lg font-semibold text-white">
                                {t('vegetable')}
                            </h3>
                            <p className="text-green-100 text-sm">
                                {t('add_vegetable_ingredients')}
                            </p>
                        </div>
                    </div>
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            showList();
                        }}
                        className="p-2 bg-white bg-opacity-20 rounded-lg hover:bg-opacity-30 transition-all duration-200"
                    >
                        <svg
                            className={`w-5 h-5 text-white transform transition-transform duration-200 ${isVegetableList ? 'rotate-180' : ''
                                }`}
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M19 9l-7 7-7-7"
                            />
                        </svg>
                    </button>
                </div>
            </div>

            {/* Content */}
            {isVegetableList && (
                <div className="p-6">
                    <div className="space-y-4 bg-gray-50 rounded-xl p-4 border border-gray-200">
                        {vegetableRows.map((row, index) => (
                            <div key={index}>
                                <div className="grid grid-cols-4 gap-4">
                                    {/* Vegetable Selection */}
                                    <div className="relative" ref={popoverRef}>
                                        {index === 0 && (
                                            <label className="text-sm font-medium text-gray-700 mb-2 block">
                                                {t('vegetable')}
                                            </label>
                                        )}
                                        <div
                                            className="w-full px-4 py-3 text-gray-900 bg-white border border-gray-200 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-200 hover:bg-gray-50 cursor-pointer flex items-center justify-between"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setSearchInput('');
                                                togglePopover(index);
                                            }}
                                        >
                                            <span
                                                className={row.vegetable ? 'text-gray-900' : 'text-gray-500'}
                                            >
                                                {row.vegetable
                                                    ? row.vegetable.name
                                                    : t('pls_sel_vegetable')}
                                            </span>
                                            <svg
                                                className="w-4 h-4 text-gray-400"
                                                fill="none"
                                                stroke="currentColor"
                                                viewBox="0 0 24 24"
                                            >
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth={2}
                                                    d="M19 9l-7 7-7-7"
                                                />
                                            </svg>
                                        </div>

                                        {/* Dropdown (Tailwind only, no inline styles or portal) */}
                                        {isPopover === index && (
                                            <div
                                                className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-auto z-[9999] w-full"
                                                onClick={(e) => e.stopPropagation()}
                                            >
                                                <div className="p-3 border-b border-gray-200">
                                                    <input
                                                        ref={searchInputRef}
                                                        type="text"
                                                        placeholder={t('search_vegetable')}
                                                        className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent outline-none text-sm"
                                                        value={searchInput}
                                                        onChange={handleSearchChange}
                                                    />
                                                </div>
                                                <div className="max-h-36 overflow-auto">
                                                    {allVegetables
                                                        ?.filter((vegetable) =>
                                                            vegetable.name
                                                                .toLowerCase()
                                                                .includes(searchInput.toLowerCase().trim())
                                                        )
                                                        .map((vegetable) => (
                                                            <div
                                                                key={vegetable._id}
                                                                className="px-3 py-2 hover:bg-green-50 cursor-pointer text-sm border-b border-gray-100 last:border-b-0"
                                                                onClick={() =>
                                                                    handleVegetableClick(index, vegetable)
                                                                }
                                                            >
                                                                {vegetable.name}
                                                            </div>
                                                        ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    {/* Unit Type */}
                                    <div className="relative">
                                        {index === 0 && (
                                            <label className="text-sm font-medium text-gray-700 mb-2 block">
                                                {t('unit_type')}
                                            </label>
                                        )}
                                        <div className="relative">
                                            <select
                                                name="unitType"
                                                value={row.unitType || ''}
                                                onChange={(e) => handleChange(index, e)}
                                                className="w-full px-4 py-3 text-gray-900 bg-white border border-gray-200 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-200 hover:bg-gray-50 appearance-none cursor-pointer"
                                            >
                                                <option value="" disabled>
                                                    {t('select_options')}
                                                </option>
                                                {unitTypeList.map((unit) => (
                                                    <option key={unit.key} value={unit.key}>
                                                        {unit.name}
                                                    </option>
                                                ))}
                                            </select>
                                            <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                                                <svg
                                                    className="w-4 h-4 text-gray-400"
                                                    fill="none"
                                                    stroke="currentColor"
                                                    viewBox="0 0 24 24"
                                                >
                                                    <path
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                        strokeWidth={2}
                                                        d="M19 9l-7 7-7-7"
                                                    />
                                                </svg>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Quantity */}
                                    <div className="relative">
                                        {index === 0 && (
                                            <label className="text-sm font-medium text-gray-700 mb-2 block">
                                                {t('Quantity')}
                                            </label>
                                        )}
                                        <input
                                            type="text"
                                            inputMode="decimal"
                                            pattern="[0-9]*"
                                            placeholder={t('Quantity')}
                                            className="w-full px-4 py-3 text-gray-900 bg-white border border-gray-200 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-200 hover:bg-gray-50"
                                            value={row.quantity}
                                            onChange={(e) => {
                                                const raw = e.target.value;
                                                const cleaned = raw
                                                    .replace(/[^0-9.]/g, '')
                                                    .replace(/(\..*)\./g, '$1');
                                                setVegetableRows((prevRows) =>
                                                    prevRows.map((r, i) =>
                                                        i === index ? { ...r, quantity: cleaned } : r
                                                    )
                                                );
                                            }}
                                        />
                                    </div>

                                    {/* Actions */}
                                    <div className="flex items-end justify-start">
                                        {vegetableRows.length - 1 === index ? (
                                            <div className="flex gap-2">
                                                <button
                                                    className="px-4 py-3 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-all duration-200 flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                                                    onClick={addNewRow}
                                                    disabled={!isLastRowFilled()}
                                                >
                                                    <FaPlus className="w-4 h-4 mr-2" />
                                                    {t('btnAddNew')}
                                                </button>
                                                <button
                                                    className="px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-all duration-200 flex items-center justify-center"
                                                    onClick={() => clearRow(index)}
                                                >
                                                    {t('btnReset')}
                                                </button>
                                            </div>
                                        ) : (
                                            <button
                                                className="px-4 py-3 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-all duration-200 flex items-center justify-center"
                                                onClick={() => removeRow(index)}
                                            >
                                                <FaTrash className="w-4 h-4" />
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}

export default VegetableItems;
